import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { TemplateType } from '../../types/Template';
import SparkCard from '../common/SparkCard';
import SparkHeading from '../common/SparkHeading';
import SparkInput from '../common/SparkInput';

interface TemplateSelectorProps {
  selectedTemplate: string | null;
  onSelectTemplate: (templateId: string, projectId: string) => void;
  customData: Record<string, any>;
  onCustomDataChange: (newData: Record<string, any>) => void;
  isEditMode?: boolean;
  /** optional pre-filled search, e.g. “pool” */
  defaultSearchQuery?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplate,
  onSelectTemplate,
  customData,
  onCustomDataChange,
  isEditMode = false,
  defaultSearchQuery,
}) => {
  const [templates, setTemplates] = useState<TemplateType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<TemplateType | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>(defaultSearchQuery ?? '');

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'templates'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const loaded = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<TemplateType, 'id'>),
        }));

        setTemplates(loaded as TemplateType[]);
        if (selectedTemplate) {
          const found = loaded.find((t) => t.id === selectedTemplate);
          if (found) setActiveTemplate(found as TemplateType);
        }
      } catch (err: any) {
        console.error('Error fetching templates:', err);
        setError('Failed to load templates. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [selectedTemplate]);

  // Activate when prop changes
  useEffect(() => {
    if (selectedTemplate && templates.length) {
      const sel = templates.find((t) => t.id === selectedTemplate);
      if (sel) setActiveTemplate(sel);
    }
  }, [selectedTemplate, templates]);

  // Keep local search in sync with defaultSearchQuery from URL
  useEffect(() => {
    if (defaultSearchQuery && defaultSearchQuery !== searchQuery) {
      setSearchQuery(defaultSearchQuery);
    }
  }, [defaultSearchQuery]);

  // Filter templates based on search query
  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const nameMatch = template.name?.toLowerCase().includes(query) || false;
    const descriptionMatch = template.description?.toLowerCase().includes(query) || false;
    
    return nameMatch || descriptionMatch;
  });

  const handleSelect = (template: TemplateType) => {
    // Prevent selection if in edit mode and this is not the current template
    if (isEditMode && activeTemplate?.id !== template.id) {
      return;
    }
    setActiveTemplate(template);
    onSelectTemplate(template.id!, template.blingsProjectId || '');
  };

  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'var(--color-text-primary)',
        }}
      >
        <div className="spark-spinner" />
        <span style={{ marginLeft: '1rem' }}>Loading templates...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="spark-message spark-message--error">{error}</div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Search Bar */}
      <div style={{ marginBottom: '24px' }}>
        <SparkInput
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search templates by name or description..."
          style={{
            width: '100%',
            fontSize: '16px',
            padding: '12px 16px',
          }}
        />
      </div>

      {/* No results message */}
      {filteredTemplates.length === 0 && searchQuery && (
        <div 
          style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: 'var(--color-text-secondary)',
            fontSize: '18px'
          }}
        >
          No templates found matching "{searchQuery}"
        </div>
      )}

      {/* Templates Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
          width: '100%',
        }}
      >
        {filteredTemplates.map((template) => {
          const isDisabled = isEditMode && activeTemplate?.id !== template.id;
          
          return (
            <SparkCard
              key={template.id}
              onClick={() => handleSelect(template)}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                border:
                  activeTemplate?.id === template.id
                    ? '3px solid var(--color-accent-blue)'
                    : '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow:
                  activeTemplate?.id === template.id
                    ? '0 0 20px rgba(62, 107, 255, 0.5)'
                    : 'var(--shadow-glow)',
                overflow: 'hidden',
                padding: 0,
                transition: 'all 0.3s ease',
                transform: activeTemplate?.id === template.id ? 'scale(1.02)' : 'scale(1)',
                opacity: isDisabled ? 0.5 : 1,
                filter: isDisabled ? 'grayscale(80%)' : 'none',
                position: 'relative',
              }}
              className={`template-card ${isDisabled ? 'disabled' : ''}`}
            >
              <img
                src={
                  template.thumbnailUrl ??
                  template.previewThumb ??
                  '/api/placeholder/320/180'
                }
                alt={template.name || 'Template'}
                style={{
                  width: '100%',
                  aspectRatio: '16/9',
                  objectFit: 'cover',
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                }}
              />
              {isDisabled && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '8px',
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      textAlign: 'center',
                      padding: '0 1rem',
                    }}
                  >
                    Template Locked
                  </div>
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.9rem',
                      textAlign: 'center',
                      padding: '0 1rem',
                    }}
                  >
                    Cannot change after event creation
                  </div>
                </div>
              )}
              <div style={{ padding: '1.25rem' }}>
                <SparkHeading level="h4" style={{ marginBottom: '0.5rem', fontSize: '1.25rem' }}>
                  {template.name}
                </SparkHeading>
                {template.description && (
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.95rem', 
                    opacity: 0.8,
                    lineHeight: 1.4 
                  }}>
                    {template.description}
                  </p>
                )}
                {activeTemplate?.id === template.id && (
                  <div style={{
                    marginTop: '0.75rem',
                    color: 'var(--color-accent-blue)',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    ✓ Selected
                  </div>
                )}
              </div>
            </SparkCard>
          );
        })}
      </div>
    </div>
  );
};

export default TemplateSelector;